import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Loader2, Clock, TrendingUp, Users as UsersIcon, Layers,
  Plus, Send, Check, X, Archive, ChevronRight, ChevronLeft,
  AlertTriangle, CheckCircle2, FileWarning, Trash2, Info, Download,
} from 'lucide-react';
import { Sidebar, Topbar, CategoryChip, TASK_CATEGORY_ORDER } from './Dashboard';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { useToast } from '../hooks/use-toast';
import { auth, tasks as tasksApi, clients as clientsApi, timesheets, getStoredUser } from '../api';
import { openPrintablePDF } from '../utils/exports';
import { AutoTimeLog } from '../components/time/AutoTimeLog';

// Build a print-ready payload for a single approved (or reviewable) timesheet
function exportTimesheetPDF(ts, firmName) {
  const dayLabel = formatDay(ts.dateIso || ts.date);
  const entries = (ts.entries || []).slice().sort((a, b) => (a.date || '').localeCompare(b.date || ''));
  const total = entries.reduce((a, e) => a + Number(e.hours || 0), 0);

  // Detail table
  const detail = {
    title: 'Entries',
    headers: ['Date', 'Category', 'Client', 'Description', 'Hours'],
    rows: entries.length > 0
      ? entries.map((e) => [
          e.date ? new Date(e.date).toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' }) : '',
          e.category || 'Other',
          e.clientName || '—',
          e.description || '—',
          Number(e.hours || 0).toFixed(2) + 'h',
        ])
      : [],
    footNote: `Total captured: ${total.toFixed(2)}h across ${entries.length} entry(ies)`,
  };

  // Category breakdown
  const catMap = {};
  entries.forEach((e) => {
    const c = e.category || 'Other';
    catMap[c] = (catMap[c] || 0) + Number(e.hours || 0);
  });
  const catRows = Object.entries(catMap)
    .sort((a, b) => b[1] - a[1])
    .map(([c, h]) => [c, h.toFixed(2) + 'h', ((h / (total || 1)) * 100).toFixed(0) + '%']);
  const category = { title: 'Category breakdown', headers: ['Category', 'Hours', '% of total'], rows: catRows };

  // Employee & approval header table
  const meta = {
    title: 'Timesheet summary',
    headers: ['Field', 'Value'],
    rows: [
      ['Employee', ts.userName || '—'],
      ['Day', dayLabel],
      ['Status', ts.status],
      ['Total hours', total.toFixed(2) + 'h'],
      ['Submitted', ts.submittedAt ? new Date(ts.submittedAt).toLocaleString() : '—'],
      ['Approved by', ts.approvedByName || (ts.status === 'Approved' ? 'Owner' : '—')],
      ['Approved at', ts.approvedAt ? new Date(ts.approvedAt).toLocaleString() : '—'],
      ...(ts.notes ? [['Note from employee', ts.notes]] : []),
    ],
  };

  openPrintablePDF({
    title: `Timesheet — ${ts.userName || 'Employee'}`,
    subtitle: `${dayLabel} · Payroll & audit copy`,
    firmName,
    sections: [meta, detail, category],
  });
}


const CATEGORIES = TASK_CATEGORY_ORDER.filter((c) => c !== 'All');

const STATUS_STYLE = {
  'Draft': 'bg-stone-100 text-stone-700 border-stone-200',
  'Submitted': 'bg-sky-50 text-sky-800 border-sky-200',
  'Approved': 'bg-emerald-50 text-emerald-800 border-emerald-200',
  'Rejected': 'bg-red-50 text-red-800 border-red-200',
};

function ymd(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function addDaysYmd(ymdStr, delta) {
  const [y, m, d] = ymdStr.split('-').map(Number);
  const dt = new Date(y, m - 1, d);
  dt.setDate(dt.getDate() + delta);
  return ymd(dt);
}

function formatDay(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleDateString(undefined, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
}

function relativeDayLabel(ymdStr) {
  const today = ymd(new Date());
  const yesterday = addDaysYmd(today, -1);
  const tomorrow = addDaysYmd(today, 1);
  if (ymdStr === today) return 'Today';
  if (ymdStr === yesterday) return 'Yesterday';
  if (ymdStr === tomorrow) return 'Tomorrow';
  return null;
}

export default function Time() {
  const nav = useNavigate();
  const { toast } = useToast();
  const [user, setUser] = useState(getStoredUser());
  const [loading, setLoading] = useState(true);
  const [taskList, setTaskList] = useState([]);
  const [clientList, setClientList] = useState([]);
  const [current, setCurrent] = useState(null);
  const [history, setHistory] = useState([]);
  const [pending, setPending] = useState([]);
  const [approvedArchive, setApprovedArchive] = useState([]);
  const [tab, setTab] = useState('current');
  const [sheetDate, setSheetDate] = useState(ymd(new Date())); // which day are we viewing/editing

  const isOwner = user?.firmRole === 'owner';

  const fetchAll = async (dateArg) => {
    try {
      const d = dateArg || sheetDate;
      const [me, tl, cl, cur, hist] = await Promise.all([
        auth.me().catch(() => null),
        tasksApi.list(),
        clientsApi.list(),
        timesheets.current(d),
        timesheets.mine(),
      ]);
      if (me) setUser(me);
      setTaskList(tl);
      setClientList(cl);
      setCurrent(cur);
      setHistory(hist);
      if ((me?.firmRole || user?.firmRole) === 'owner') {
        const [pend, arch] = await Promise.all([
          timesheets.pending().catch(() => []),
          timesheets.firmApproved().catch(() => []),
        ]);
        setPending(pend);
        setApprovedArchive(arch);
      }
    } catch (err) {
      // 401 interceptor handles
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!localStorage.getItem('ad_token')) { nav('/login'); return; }
    fetchAll();
    // eslint-disable-next-line
  }, []);

  // Reload only the current sheet when the date changes
  useEffect(() => {
    if (loading) return;
    (async () => {
      try {
        const cur = await timesheets.current(sheetDate);
        setCurrent(cur);
      } catch { /* ignore */ }
    })();
    // eslint-disable-next-line
  }, [sheetDate]);

  if (loading) return <FullscreenLoading label="Loading timesheets..."/>;

  return (
    <div className="min-h-screen bg-stone-50 flex">
      <Sidebar activeLabel="Time"/>
      <div className="flex-1 min-w-0">
        <Topbar user={user} firm={user?.firm}/>
        <main className="p-4 lg:p-8 space-y-6">
          <div>
            <h1 className="text-2xl lg:text-3xl font-serif font-semibold text-stone-900 flex items-center gap-2">
              <Clock size={24} className="text-emerald-800"/> Time &amp; Timesheets
            </h1>
            <p className="mt-1 text-stone-600 text-sm">Capture your work day by day, submit for approval, and see the firm's utilisation.</p>
          </div>

          <Tabs value={tab} onValueChange={setTab} data-testid="time-tabs">
            <TabsList className="bg-white border border-stone-200">
              <TabsTrigger value="current" data-testid="tab-current">My Timesheet</TabsTrigger>
              <TabsTrigger value="history" data-testid="tab-history">History <Badge variant="outline" className="ml-2 h-5 px-1.5 text-[10px] border-stone-300">{history.filter((h) => h.status !== 'Approved').length}</Badge></TabsTrigger>
              {isOwner && <TabsTrigger value="approvals" data-testid="tab-approvals">
                Approvals {pending.length > 0 && <Badge className="ml-2 h-5 px-1.5 text-[10px] bg-sky-600 hover:bg-sky-600">{pending.length}</Badge>}
              </TabsTrigger>}
              <TabsTrigger value="insights" data-testid="tab-insights">Insights</TabsTrigger>
              <TabsTrigger value="auto" data-testid="tab-auto-time">Auto time</TabsTrigger>
            </TabsList>

            <TabsContent value="current" className="mt-6">
              <CurrentTimesheet
                sheet={current}
                clientList={clientList}
                taskList={taskList}
                userId={user?.id}
                sheetDate={sheetDate}
                onDateChange={setSheetDate}
                onChanged={() => fetchAll(sheetDate)}
              />
            </TabsContent>

            <TabsContent value="history" className="mt-6">
              <HistoryList history={history} firmName={user?.firm} currentUserName={user?.name}/>
            </TabsContent>

            {isOwner && (
              <TabsContent value="approvals" className="mt-6">
                <ApprovalsList pending={pending} approvedArchive={approvedArchive} firmName={user?.firm} onChanged={fetchAll}/>
              </TabsContent>
            )}

            <TabsContent value="insights" className="mt-6">
              <InsightsView taskList={taskList}/>
            </TabsContent>

            <TabsContent value="auto" className="mt-6">
              <AutoTimeLog isOwner={isOwner}/>
            </TabsContent>
          </Tabs>
        </main>
      </div>
    </div>
  );
}

// ------------------------ Current Timesheet (daily) ------------------------
function CurrentTimesheet({ sheet, clientList, taskList, userId, sheetDate, onDateChange, onChanged }) {
  const { toast } = useToast();
  const [entries, setEntries] = useState(sheet?.entries || []);
  const [notes, setNotes] = useState(sheet?.notes || '');
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [addOpen, setAddOpen] = useState(false);

  // Re-sync when the server sheet (or day) changes
  useEffect(() => {
    setEntries(sheet?.entries || []);
    setNotes(sheet?.notes || '');
  }, [sheet?.id, sheet?.status, sheet?.dateIso]);

  if (!sheet) return <div className="text-sm text-stone-500">No timesheet yet.</div>;

  const editable = sheet.status === 'Draft' || sheet.status === 'Rejected';
  const dayTotal = entries.reduce((a, e) => a + Number(e.hours || 0), 0);
  const relLabel = relativeDayLabel(sheetDate);
  const todayYmd = ymd(new Date());
  const isFuture = sheetDate > todayYmd;

  const goPrev = () => onDateChange(addDaysYmd(sheetDate, -1));
  const goNext = () => onDateChange(addDaysYmd(sheetDate, 1));
  const goToday = () => onDateChange(todayYmd);

  const persist = async (nextEntries, nextNotes) => {
    setSaving(true);
    try {
      await timesheets.update(sheet.id, { entries: nextEntries, notes: nextNotes });
      onChanged?.();
    } catch (err) {
      toast({ title: 'Save failed', description: err?.response?.data?.detail || 'Try again.', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const addEntry = async (entry) => {
    const next = [...entries, { ...entry, id: crypto.randomUUID?.() || `${Date.now()}-${Math.random()}` }];
    setEntries(next);
    await persist(next, notes);
  };

  const removeEntry = async (idx) => {
    const next = entries.filter((_, i) => i !== idx);
    setEntries(next);
    await persist(next, notes);
  };

  const saveNotes = async () => { await persist(entries, notes); };

  const submit = async () => {
    if (entries.length === 0) {
      toast({ title: 'Nothing to submit', description: 'Add at least one entry first.', variant: 'destructive' });
      return;
    }
    setSubmitting(true);
    try {
      await persist(entries, notes); // save first
      await timesheets.submit(sheet.id);
      toast({ title: 'Submitted for approval', description: 'Your firm owner will review shortly.' });
      onChanged?.();
    } catch (err) {
      toast({ title: 'Submit failed', description: err?.response?.data?.detail || 'Try again.', variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="current-timesheet">
      {/* Day navigator */}
      <div className="rounded-xl bg-white border border-stone-200 p-4 flex items-center gap-3 flex-wrap">
        <Button variant="outline" size="sm" onClick={goPrev} className="h-9 px-2" data-testid="day-prev-btn" title="Previous day">
          <ChevronLeft size={16}/>
        </Button>
        <div className="flex-1 min-w-[180px]">
          <div className="flex items-center gap-2">
            <input
              type="date"
              value={sheetDate}
              max={todayYmd}
              onChange={(e) => e.target.value && onDateChange(e.target.value)}
              className="h-9 px-2 rounded-md border border-stone-200 bg-white text-sm"
              data-testid="day-picker"
            />
            {relLabel && <Badge variant="outline" className="h-6 border-emerald-200 bg-emerald-50 text-emerald-800 text-[11px]">{relLabel}</Badge>}
            {sheetDate !== todayYmd && (
              <button onClick={goToday} className="text-xs text-sky-700 hover:text-sky-800 underline underline-offset-2" data-testid="day-today-btn">Jump to today</button>
            )}
          </div>
          <div className="mt-1 text-xs text-stone-500">
            Each day is its own timesheet — submitting one day never locks another.
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={goNext} disabled={isFuture} className="h-9 px-2" data-testid="day-next-btn" title="Next day">
          <ChevronRight size={16}/>
        </Button>
      </div>

      {/* Header card */}
      <div className="rounded-xl bg-white border border-stone-200 overflow-hidden">
        <div className="p-5 flex flex-col lg:flex-row lg:items-center gap-4">
          <div>
            <div className="text-xs uppercase tracking-widest text-sky-700 font-semibold">Timesheet · {relLabel || 'Day'}</div>
            <div className="mt-1 font-serif text-2xl font-semibold text-stone-900">{formatDay(sheet.dateIso)}</div>
            <div className="mt-1 flex items-center gap-2 text-xs">
              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[11px] font-medium ${STATUS_STYLE[sheet.status]}`} data-testid="timesheet-status">
                {sheet.status}
              </span>
              {sheet.submittedAt && sheet.status === 'Submitted' && (
                <span className="text-stone-500">Submitted {new Date(sheet.submittedAt).toLocaleString()}</span>
              )}
              {sheet.approvedAt && sheet.status === 'Approved' && (
                <span className="text-emerald-700">Approved by {sheet.approvedByName} · {new Date(sheet.approvedAt).toLocaleDateString()}</span>
              )}
            </div>
            {sheet.status === 'Rejected' && sheet.rejectionNote && (
              <div className="mt-2 max-w-lg rounded-lg bg-red-50 border border-red-100 p-3 text-xs text-red-800 flex items-start gap-2">
                <FileWarning size={14} className="mt-0.5 flex-shrink-0"/>
                <div>
                  <div className="font-semibold">Returned for revision:</div>
                  <div className="mt-0.5">{sheet.rejectionNote}</div>
                </div>
              </div>
            )}
          </div>
          <div className="lg:ml-auto grid grid-cols-2 gap-3 text-center">
            <MiniStat label="Day entries" value={`${dayTotal.toFixed(2)}h`}/>
            <MiniStat label="Task-timer total" value={`${(sheet.taskTimerHours || 0).toFixed(2)}h`} sub="lifetime" accent/>
          </div>
        </div>

        {(sheet.taskTimerHours || 0) > 0 && (
          <div className="px-5 py-3 border-t border-stone-100 bg-sky-50/40 text-xs text-sky-900 flex items-start gap-2">
            <Info size={13} className="mt-0.5 flex-shrink-0"/>
            <div>Task-timer hours ({(sheet.taskTimerHours).toFixed(2)}h) are already tracked separately when you use the Start/Stop buttons on tasks. Use this timesheet for work not logged on a task.</div>
          </div>
        )}
      </div>

      {/* Entries */}
      <div className="rounded-xl bg-white border border-stone-200 overflow-hidden">
        <div className="p-5 border-b border-stone-100 flex items-center justify-between">
          <div>
            <div className="font-semibold text-stone-900">Entries for this day</div>
            <div className="text-xs text-stone-500 mt-0.5">Log any work not already captured by a task timer.</div>
          </div>
          {editable && (
            <Button onClick={() => setAddOpen(true)} className="bg-emerald-800 hover:bg-emerald-900 text-white h-9" data-testid="add-entry-btn">
              <Plus size={14} className="mr-1.5"/> Add entry
            </Button>
          )}
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-stone-50 text-stone-600 text-xs uppercase tracking-wider">
              <tr>
                <th className="text-left px-5 py-3 font-medium">Category</th>
                <th className="text-left px-5 py-3 font-medium">Client</th>
                <th className="text-left px-5 py-3 font-medium">Description</th>
                <th className="text-right px-5 py-3 font-medium">Hours</th>
                {editable && <th className="px-5 py-3"></th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-stone-100">
              {entries.length === 0 && (
                <tr><td colSpan={editable ? 5 : 4} className="px-5 py-10 text-center text-stone-500">
                  No entries for this day. {editable ? <button onClick={() => setAddOpen(true)} className="text-sky-700 hover:text-sky-800 font-medium underline underline-offset-2">Add one</button> : 'Nothing to show.'}
                </td></tr>
              )}
              {entries.slice().sort((a, b) => (a.description || '').localeCompare(b.description || '')).map((e, idx) => (
                <tr key={e.id || idx} className="hover:bg-stone-50" data-testid={`entry-row-${idx}`}>
                  <td className="px-5 py-3"><CategoryChip category={e.category || 'Other'}/></td>
                  <td className="px-5 py-3 text-stone-700">{e.clientName || <span className="text-stone-400">—</span>}</td>
                  <td className="px-5 py-3 text-stone-700 max-w-xs truncate">{e.description || <span className="text-stone-400">—</span>}</td>
                  <td className="px-5 py-3 text-right tabular-nums font-medium text-stone-900">{Number(e.hours || 0).toFixed(2)}h</td>
                  {editable && (
                    <td className="px-5 py-3 text-right">
                      <button onClick={() => removeEntry(entries.findIndex((x) => x === e))} className="text-stone-400 hover:text-red-600" title="Remove"><Trash2 size={14}/></button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Notes + Submit */}
      <div className="rounded-xl bg-white border border-stone-200 p-5">
        <Label htmlFor="ts-notes">Note to your approver <span className="text-stone-400 font-normal">(optional)</span></Label>
        <Textarea
          id="ts-notes"
          rows={3}
          value={notes}
          disabled={!editable}
          onChange={(e) => setNotes(e.target.value)}
          onBlur={saveNotes}
          placeholder="Any context the owner should know before approving?"
          className="mt-1.5"
          data-testid="timesheet-notes"
        />

        <div className="mt-5 flex items-center justify-between gap-3">
          <div className="text-xs text-stone-500 flex items-center gap-2">
            {saving && <><Loader2 size={12} className="animate-spin"/> Saving…</>}
            {!saving && editable && <>Changes auto-save as you add entries.</>}
            {!editable && <><CheckCircle2 size={12} className="text-emerald-600"/> This day is locked while {sheet.status === 'Submitted' ? 'pending approval' : 'archived'}.</>}
          </div>
          {editable ? (
            <Button
              onClick={submit}
              disabled={submitting || entries.length === 0}
              className="bg-emerald-800 hover:bg-emerald-900 text-white h-10"
              data-testid="submit-timesheet-btn"
            >
              {submitting ? <Loader2 size={14} className="mr-2 animate-spin"/> : <Send size={14} className="mr-2"/>}
              Submit this day
            </Button>
          ) : sheet.status === 'Submitted' ? (
            <div className="text-sm text-sky-700 font-medium flex items-center gap-2">
              <Loader2 size={14} className="animate-spin"/> Awaiting owner review
            </div>
          ) : sheet.status === 'Approved' ? (
            <div className="text-sm text-emerald-700 font-medium flex items-center gap-2">
              <Archive size={14}/> Archived
            </div>
          ) : null}
        </div>
      </div>

      <AddEntryDialog
        open={addOpen}
        onOpenChange={setAddOpen}
        clientList={clientList}
        taskList={taskList}
        defaultDate={sheetDate}
        onAdd={async (entry) => { await addEntry(entry); }}
      />
    </div>
  );
}

function MiniStat({ label, value, sub, accent }) {
  return (
    <div className={`rounded-lg px-3 py-2 ${accent ? 'bg-emerald-800 text-white' : 'bg-stone-50 text-stone-900'}`}>
      <div className={`text-[10px] uppercase tracking-wider ${accent ? 'text-emerald-100' : 'text-stone-500'}`}>{label}</div>
      <div className={`mt-0.5 text-lg font-serif font-semibold tabular-nums ${accent ? 'text-white' : ''}`}>{value}</div>
      {sub && <div className={`text-[10px] ${accent ? 'text-emerald-100' : 'text-stone-400'}`}>{sub}</div>}
    </div>
  );
}

function AddEntryDialog({ open, onOpenChange, clientList, taskList, defaultDate, onAdd }) {
  const [form, setForm] = useState({ clientId: '', taskId: '', category: 'Other', description: '', hours: 1 });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) setForm({ clientId: '', taskId: '', category: 'Other', description: '', hours: 1 });
  }, [open, defaultDate]);

  const relevantTasks = taskList.filter((t) => !form.clientId || t.clientId === form.clientId);

  const submit = async (e) => {
    e.preventDefault();
    if (!(form.hours > 0)) return;
    setBusy(true);
    try {
      const t = form.taskId ? taskList.find((x) => x.id === form.taskId) : null;
      const c = form.clientId ? clientList.find((x) => x.id === form.clientId) : null;
      await onAdd({
        date: defaultDate,
        clientId: form.clientId || null,
        clientName: c?.name || '',
        taskId: form.taskId || null,
        category: (t?.category) || form.category || 'Other',
        description: form.description || (t?.title || ''),
        hours: Number(form.hours) || 0,
      });
      onOpenChange(false);
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="add-entry-dialog">
        <DialogHeader>
          <DialogTitle>Log time for {formatDay(defaultDate)}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="entry-hours">Hours</Label>
              <Input id="entry-hours" type="number" min="0.25" step="0.25" required value={form.hours} onChange={(e) => setForm({ ...form, hours: e.target.value })} className="mt-1.5" data-testid="entry-hours"/>
            </div>
            <div>
              <Label>Category</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger className="mt-1.5"><SelectValue/></SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label>Client</Label>
            <Select value={form.clientId || 'none'} onValueChange={(v) => setForm({ ...form, clientId: v === 'none' ? '' : v, taskId: '' })}>
              <SelectTrigger className="mt-1.5"><SelectValue placeholder="Select client"/></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No client</SelectItem>
                {clientList.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {relevantTasks.length > 0 && (
            <div>
              <Label>Link to a task <span className="text-stone-400 font-normal">(optional)</span></Label>
              <Select value={form.taskId || 'none'} onValueChange={(v) => setForm({ ...form, taskId: v === 'none' ? '' : v })}>
                <SelectTrigger className="mt-1.5"><SelectValue placeholder="Freeform entry"/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Freeform entry (not on a task)</SelectItem>
                  {relevantTasks.slice(0, 50).map((t) => <SelectItem key={t.id} value={t.id}>{t.title}{t.clientName ? ` — ${t.clientName}` : ''}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <div>
            <Label htmlFor="entry-desc">Description</Label>
            <Textarea id="entry-desc" rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="What did you work on?" className="mt-1.5" data-testid="entry-description"/>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={busy} className="bg-emerald-800 hover:bg-emerald-900 text-white" data-testid="entry-save-btn">
              {busy ? <Loader2 size={14} className="mr-2 animate-spin"/> : <Plus size={14} className="mr-2"/>} Add entry
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ------------------------ History ------------------------
function HistoryList({ history, firmName, currentUserName }) {
  if (!history || history.length === 0) {
    return (
      <div className="rounded-xl bg-white border border-stone-200 p-10 text-center text-sm text-stone-500">
        No submitted timesheets yet.
      </div>
    );
  }
  return (
    <div className="rounded-xl bg-white border border-stone-200 overflow-hidden">
      <div className="p-5 border-b border-stone-100">
        <div className="font-semibold text-stone-900">Your timesheet history</div>
        <div className="text-xs text-stone-500 mt-0.5">Every day you've captured, with approval status.</div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-stone-50 text-stone-600 text-xs uppercase tracking-wider">
            <tr>
              <th className="text-left px-5 py-3 font-medium">Day</th>
              <th className="text-right px-5 py-3 font-medium">Hours</th>
              <th className="text-right px-5 py-3 font-medium">Entries</th>
              <th className="text-left px-5 py-3 font-medium">Status</th>
              <th className="text-left px-5 py-3 font-medium">Reviewed</th>
              <th className="px-5 py-3"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-stone-100">
            {history.map((h) => (
              <tr key={h.id} className="hover:bg-stone-50" data-testid={`history-row-${h.id}`}>
                <td className="px-5 py-3 font-medium text-stone-900">{formatDay(h.dateIso || h.date)}</td>
                <td className="px-5 py-3 text-right tabular-nums text-stone-900">{(h.totalHours || 0).toFixed(2)}h</td>
                <td className="px-5 py-3 text-right text-stone-600">{(h.entries || []).length}</td>
                <td className="px-5 py-3">
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[11px] font-medium ${STATUS_STYLE[h.status]}`}>
                    {h.status === 'Approved' && <Archive size={11}/>}
                    {h.status}
                  </span>
                </td>
                <td className="px-5 py-3 text-stone-600 text-xs">
                  {h.status === 'Approved' && h.approvedAt && <>by {h.approvedByName} · {new Date(h.approvedAt).toLocaleDateString()}</>}
                  {h.status === 'Submitted' && h.submittedAt && <>submitted {new Date(h.submittedAt).toLocaleDateString()}</>}
                  {h.status === 'Rejected' && h.rejectionNote && <span className="text-red-700">Returned: {h.rejectionNote}</span>}
                  {h.status === 'Draft' && <span className="text-stone-400">Not submitted</span>}
                </td>
                <td className="px-5 py-3 text-right">
                  {h.status === 'Approved' && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-8 border-stone-300 text-stone-700 hover:text-sky-700 hover:border-sky-400"
                      onClick={() => exportTimesheetPDF({ ...h, userName: h.userName || currentUserName }, firmName)}
                      data-testid={`history-export-${h.id}`}
                    >
                      <Download size={13} className="mr-1.5"/> PDF
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function addDays(iso, n) { // kept for compatibility if ever needed
  const d = new Date(iso);
  d.setDate(d.getDate() + n);
  return d.toISOString();
}

// ------------------------ Approvals (owner) ------------------------
function ApprovalsList({ pending, approvedArchive, firmName, onChanged }) {
  const { toast } = useToast();
  const [busyId, setBusyId] = useState(null);
  const [rejectFor, setRejectFor] = useState(null);
  const [rejectNote, setRejectNote] = useState('');

  const approve = async (ts) => {
    setBusyId(ts.id);
    try {
      await timesheets.approve(ts.id);
      toast({ title: 'Approved', description: `${ts.userName}'s timesheet is archived.` });
      onChanged?.();
    } catch (err) {
      toast({ title: 'Approve failed', description: err?.response?.data?.detail || 'Try again.', variant: 'destructive' });
    } finally {
      setBusyId(null);
    }
  };

  const submitReject = async () => {
    if (!rejectFor) return;
    setBusyId(rejectFor.id);
    try {
      await timesheets.reject(rejectFor.id, rejectNote);
      toast({ title: 'Returned for revision', description: `${rejectFor.userName} will see your note.` });
      setRejectFor(null);
      setRejectNote('');
      onChanged?.();
    } catch (err) {
      toast({ title: 'Reject failed', description: err?.response?.data?.detail || 'Try again.', variant: 'destructive' });
    } finally {
      setBusyId(null);
    }
  };

  if (!pending || pending.length === 0) {
    return (
      <div className="space-y-6">
        <div className="rounded-xl bg-white border border-stone-200 p-10 text-center text-sm text-stone-500" data-testid="empty-approvals">
          <CheckCircle2 size={22} className="mx-auto text-emerald-500 mb-2"/>
          No timesheets waiting for approval.
        </div>
        <ApprovedArchive rows={approvedArchive} firmName={firmName}/>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="approvals-list">
      <div className="space-y-4">
      {pending.map((ts) => {
        const initials = (ts.userName || 'U').split(' ').map((s) => s[0]).slice(0, 2).join('').toUpperCase();
        const catBreakdown = {};
        (ts.entries || []).forEach((e) => {
          const c = e.category || 'Other';
          catBreakdown[c] = (catBreakdown[c] || 0) + Number(e.hours || 0);
        });
        return (
          <div key={ts.id} className="rounded-xl bg-white border border-stone-200 overflow-hidden" data-testid={`pending-${ts.id}`}>
            <div className="p-5 flex flex-col sm:flex-row sm:items-center gap-4 border-b border-stone-100">
              <Avatar className="h-10 w-10"><AvatarFallback className="bg-sky-100 text-sky-900 text-xs font-semibold">{initials}</AvatarFallback></Avatar>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-stone-900">{ts.userName}</div>
                <div className="text-xs text-stone-500">{formatDay(ts.dateIso || ts.date)} · Submitted {new Date(ts.submittedAt).toLocaleString()}</div>
              </div>
              <div className="text-right">
                <div className="text-2xl font-serif font-semibold text-stone-900 tabular-nums">{(ts.totalHours || 0).toFixed(2)}h</div>
                <div className="text-xs text-stone-500">{(ts.entries || []).length} entries</div>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setRejectFor(ts)} disabled={busyId === ts.id} className="border-red-200 text-red-700 hover:bg-red-50 hover:text-red-800" data-testid={`reject-btn-${ts.id}`}>
                  <X size={14} className="mr-1.5"/> Return
                </Button>
                <Button onClick={() => approve(ts)} disabled={busyId === ts.id} className="bg-emerald-800 hover:bg-emerald-900 text-white" data-testid={`approve-btn-${ts.id}`}>
                  {busyId === ts.id ? <Loader2 size={14} className="mr-1.5 animate-spin"/> : <Check size={14} className="mr-1.5"/>} Approve
                </Button>
              </div>
            </div>
            {/* Category breakdown */}
            {Object.keys(catBreakdown).length > 0 && (
              <div className="px-5 py-3 flex flex-wrap gap-1.5 border-b border-stone-100 bg-stone-50/40">
                {Object.entries(catBreakdown).sort((a, b) => b[1] - a[1]).map(([c, h]) => (
                  <span key={c} className="inline-flex items-center gap-1.5 text-xs">
                    <CategoryChip category={c}/>
                    <span className="tabular-nums text-stone-700 font-medium">{h.toFixed(2)}h</span>
                  </span>
                ))}
              </div>
            )}
            {/* Entries */}
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-stone-50 text-stone-600 text-xs uppercase tracking-wider">
                  <tr>
                    <th className="text-left px-5 py-2 font-medium">Date</th>
                    <th className="text-left px-5 py-2 font-medium">Category</th>
                    <th className="text-left px-5 py-2 font-medium">Client</th>
                    <th className="text-left px-5 py-2 font-medium">Description</th>
                    <th className="text-right px-5 py-2 font-medium">Hours</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-stone-100">
                  {(ts.entries || []).slice().sort((a, b) => (a.date || '').localeCompare(b.date || '')).map((e, i) => (
                    <tr key={i}>
                      <td className="px-5 py-2 text-stone-700 whitespace-nowrap">{e.date && new Date(e.date).toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' })}</td>
                      <td className="px-5 py-2"><CategoryChip category={e.category || 'Other'}/></td>
                      <td className="px-5 py-2 text-stone-700">{e.clientName || '—'}</td>
                      <td className="px-5 py-2 text-stone-700 max-w-md truncate">{e.description || '—'}</td>
                      <td className="px-5 py-2 text-right tabular-nums font-medium text-stone-900">{Number(e.hours || 0).toFixed(2)}h</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {ts.notes && (
              <div className="px-5 py-3 bg-sky-50/40 border-t border-stone-100 text-sm text-stone-700">
                <span className="font-medium text-sky-900">Note from {ts.userName?.split(' ')[0]}:</span> {ts.notes}
              </div>
            )}
          </div>
        );
      })}
      </div>

      <ApprovedArchive rows={approvedArchive} firmName={firmName}/>

      {/* Reject dialog */}
      <Dialog open={!!rejectFor} onOpenChange={(v) => { if (!v) { setRejectFor(null); setRejectNote(''); } }}>
        <DialogContent className="sm:max-w-md" data-testid="reject-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><AlertTriangle size={18} className="text-red-600"/> Return timesheet</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-stone-600">Return {rejectFor?.userName}'s timesheet for revision. Add a short note so they know what to fix.</p>
            <Textarea
              rows={4}
              value={rejectNote}
              onChange={(e) => setRejectNote(e.target.value)}
              placeholder="e.g. Please split the 8h on Friday into individual client entries."
              data-testid="reject-note"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectFor(null)}>Cancel</Button>
            <Button onClick={submitReject} className="bg-red-600 hover:bg-red-700 text-white" data-testid="confirm-reject-btn">
              <X size={14} className="mr-1.5"/> Return for revision
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ------------------------ Approved archive (owner, payroll trail) ------------------------
function ApprovedArchive({ rows, firmName }) {
  if (!rows || rows.length === 0) {
    return (
      <div className="rounded-xl bg-white border border-stone-200 p-6 text-sm text-stone-500 flex items-center gap-2" data-testid="empty-archive">
        <Archive size={14} className="text-stone-400"/> No approved timesheets in the archive yet.
      </div>
    );
  }
  return (
    <div className="rounded-xl bg-white border border-stone-200 overflow-hidden" data-testid="approved-archive">
      <div className="p-5 border-b border-stone-100 flex items-center justify-between">
        <div>
          <div className="font-semibold text-stone-900 flex items-center gap-2"><Archive size={16} className="text-emerald-800"/> Approved archive</div>
          <div className="text-xs text-stone-500 mt-0.5">Payroll & audit trail — every approved timesheet across the firm.</div>
        </div>
        <Badge variant="outline" className="border-stone-300 text-stone-700">{rows.length} archived</Badge>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-stone-50 text-stone-600 text-xs uppercase tracking-wider">
            <tr>
              <th className="text-left px-5 py-3 font-medium">Employee</th>
              <th className="text-left px-5 py-3 font-medium">Day</th>
              <th className="text-right px-5 py-3 font-medium">Hours</th>
              <th className="text-left px-5 py-3 font-medium">Approved</th>
              <th className="px-5 py-3"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-stone-100">
            {rows.map((ts) => (
              <tr key={ts.id} className="hover:bg-stone-50" data-testid={`archive-row-${ts.id}`}>
                <td className="px-5 py-3 font-medium text-stone-900">{ts.userName || '—'}</td>
                <td className="px-5 py-3 text-stone-700">{formatDay(ts.dateIso || ts.date)}</td>
                <td className="px-5 py-3 text-right tabular-nums text-stone-900">{(ts.totalHours || 0).toFixed(2)}h</td>
                <td className="px-5 py-3 text-stone-600 text-xs">
                  by {ts.approvedByName || 'Owner'} · {ts.approvedAt ? new Date(ts.approvedAt).toLocaleDateString() : '—'}
                </td>
                <td className="px-5 py-3 text-right">
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-8 border-stone-300 text-stone-700 hover:text-sky-700 hover:border-sky-400"
                    onClick={() => exportTimesheetPDF(ts, firmName)}
                    data-testid={`archive-export-${ts.id}`}
                  >
                    <Download size={13} className="mr-1.5"/> PDF
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ------------------------ Insights (original analytics view) ------------------------
function InsightsView({ taskList }) {
  const [range, setRange] = useState('all');
  const filtered = useMemo(() => {
    if (range === 'all') return taskList;
    const now = new Date();
    const days = range === 'week' ? 7 : 31;
    const from = new Date(now.getTime() - days * 24 * 60 * 60 * 1000);
    return taskList.filter((t) => t.dueDate && new Date(t.dueDate) >= from);
  }, [taskList, range]);
  const totalHours = filtered.reduce((a, t) => a + (t.hours || 0), 0);
  const unbilled = filtered.filter((t) => !t.invoicedInInvoiceId).reduce((a, t) => a + (t.hours || 0), 0);
  const byCategory = groupBy(filtered, (t) => t.category || 'Other');
  const byAssignee = groupBy(filtered, (t) => t.assignee || 'Unassigned');
  const byClient = groupBy(filtered, (t) => t.clientName || 'No client');

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-end">
        <div className="inline-flex rounded-lg border border-stone-200 bg-white p-1">
          {[['week', 'Last 7 days'], ['month', 'Last 31 days'], ['all', 'All time']].map(([v, l]) => (
            <button key={v} type="button" onClick={() => setRange(v)} className={`px-3 py-1.5 rounded-md text-sm font-medium ${range === v ? 'bg-emerald-800 text-white' : 'text-stone-600 hover:text-stone-900'}`}>{l}</button>
          ))}
        </div>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <SmallStatCard icon={Clock} label="Total hours" value={`${totalHours.toFixed(2)}h`}/>
        <SmallStatCard icon={TrendingUp} label="Unbilled" value={`${unbilled.toFixed(2)}h`} sub={`≈ R${Math.round(unbilled * 1100).toLocaleString()}`}/>
        <SmallStatCard icon={UsersIcon} label="Teammates active" value={Object.keys(byAssignee).filter((k) => byAssignee[k].hours > 0).length || 0}/>
        <SmallStatCard icon={Layers} label="Categories used" value={Object.keys(byCategory).filter((k) => byCategory[k].hours > 0).length || 0}/>
      </div>
      <div className="grid lg:grid-cols-2 gap-6">
        <BreakdownCard title="By teammate" rows={byAssignee} kind="assignee"/>
        <BreakdownCard title="By client" rows={byClient} kind="client"/>
      </div>
      <BreakdownCard title="By category" rows={byCategory} kind="category" wide/>
    </div>
  );
}

function groupBy(items, keyFn) {
  const map = {};
  for (const t of items) {
    const key = keyFn(t);
    if (!map[key]) map[key] = { hours: 0, count: 0 };
    map[key].hours += t.hours || 0;
    map[key].count += 1;
  }
  return map;
}

function SmallStatCard({ icon: Icon, label, value, sub }) {
  return (
    <div className="rounded-xl bg-white border border-stone-200 p-5 hover:shadow-md transition-shadow">
      <div className="flex items-center gap-2 text-stone-500 text-xs font-medium"><Icon size={14}/> {label}</div>
      <div className="mt-2 text-3xl font-serif font-semibold text-stone-900 tabular-nums">{value}</div>
      {sub && <div className="mt-1 text-xs text-stone-500">{sub}</div>}
    </div>
  );
}

function BreakdownCard({ title, rows, kind }) {
  const entries = Object.entries(rows || {}).sort((a, b) => b[1].hours - a[1].hours);
  const maxHours = entries.reduce((m, [, v]) => Math.max(m, v.hours), 0.01);
  return (
    <div className="rounded-xl bg-white border border-stone-200 overflow-hidden">
      <div className="p-5 border-b border-stone-100 font-semibold text-stone-900">{title}</div>
      <div className="p-5 space-y-3">
        {entries.length === 0 && <div className="text-sm text-stone-500">No hours logged in this range yet.</div>}
        {entries.map(([label, data]) => (
          <div key={label}>
            <div className="flex items-center justify-between text-sm">
              <div className="flex items-center gap-2 text-stone-900 truncate">
                {kind === 'category' ? <CategoryChip category={label}/> : <span className="font-medium">{label}</span>}
                <span className="text-xs text-stone-500">{data.count} task{data.count === 1 ? '' : 's'}</span>
              </div>
              <div className="font-semibold text-stone-900 tabular-nums">{data.hours.toFixed(2)}h</div>
            </div>
            <div className="mt-1.5 h-2 rounded-full bg-stone-100 overflow-hidden">
              <div className="h-full bg-emerald-700" style={{ width: `${(data.hours / maxHours) * 100}%` }}/>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function FullscreenLoading({ label = 'Loading...' }) {
  return (
    <div className="min-h-screen bg-stone-50 grid place-items-center">
      <div className="flex items-center gap-2 text-stone-600"><Loader2 className="animate-spin" size={18}/> {label}</div>
    </div>
  );
}
